import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@app/store";
import { fetchTemplates, deleteTemplate } from "./TemplateSlice";
import DataTable from "@components/ui/DataTable";
import type { Column } from "@components/ui/DataTable";
import { TemplateOutputDTO } from "./types";
import { LoadingSkeleton } from "@components/ui/LoadingSkeleton";
import { ErrorDisplay } from "@components/ui/ErrorDisplay";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchAffiliates } from "../affiliate/AffiliateSlice";
import { fetchCategoryDocuments } from "../categorydocument";
import { Article } from "@mui/icons-material";
import { IconRight } from "react-day-picker";
import ApplyTags from "./ApplyTags";

const TemplateList = (props: any) => {
  console.log("🔄 Renderizando TemplateList");
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const { items, loading, error } = useSelector((state: RootState) => {
    console.log("🔍 Selector executando, loading:", state.template.loading);
    return state.template;
  });

  const { items: categories } = useSelector((state: RootState) => {
    console.log(
      "🔍 Selector executando, loading:",
      state.categorydocument.loading
    );
    return state.categorydocument;
  });

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filters] = useState<Record<string, string>>({});
  const [modal, setModal] = useState(false);
  const [template, setTemplate] = useState<TemplateOutputDTO | null>(null);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  const loadData = useCallback(
    async (currentFilters: Record<string, string>) => {
      // Avoid simultaneous calls
      if (isLoadingRef.current) {
        console.log("🚫 Requisição em andamento, ignorando nova chamada");
        return;
      }

      await dispatch(fetchCategoryDocuments({}));

      console.log("📡 Iniciando requisição fetchTemplates");
      isLoadingRef.current = true;

      try {
        await dispatch(fetchTemplates(currentFilters));
        await dispatch(fetchAffiliates({}));

        initialLoadDoneRef.current = true;
      } finally {
        isLoadingRef.current = false;
      }
    },
    [dispatch]
  );

  useEffect(() => {
    console.log("🔄 useEffect - Carregando dados iniciais");
    loadData(filters);
    setIsInitialLoad(false);
  }, [loadData, filters]);

  useEffect(() => {
    if (props.setTotal) {
      props.setTotal(items?.length);
    }
  }, [items, props]);

  const handleDelete = async (id: string) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      await dispatch(deleteTemplate(deleteId));
      setDeleteId(null);
      loadData(filters);
    }
  };

  const cancelDelete = () => {
    setDeleteId(null);
  };

  const columns: Column<TemplateOutputDTO>[] = [
    {
      image: "photoUrl",
      placeholderImage: "./documents.svg",
      style_image: {
        borderRadius: 10,
        border: "1px solid lightgrey",
        background: "rgb(244, 244, 244)",
        width: 30,
        height: 30,
        objectFit: "contain",
      },
      key: "name" as keyof TemplateOutputDTO,
      header: "Nome",
      render: (value: any) => String(value || ""),
    },
    {
      key: "category",
      header: "Categoria",
      render: (value: any) => {
        return (
          categories.filter((item: any) => item._id == value)[0]?.name ?? ""
        );
      },
    },
  ];

  if (loading && isInitialLoad) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <div
      className={
        "flex flex-col flex-1 shrink  self-stretch  basis-0 min-w-60 max-md:px-5 bg-background-dark " +
        (props.hide_header != true ? " px-6 py-7" : "")
      }
    >
      <DataTable
        hide_header={props.hide_header}
        columns={columns}
        data={items}
        hide_show
        title={"Templates"}
        subtitle={"Lista de templates cadatrados no sistema"}
        basePath="templates"
        onDelete={handleDelete}
        loading={loading}
        error={error}
        newFormat
        actions={[
          {
            label: "Configurar Tags",
            icon: <Article />,
            onClick: (item: TemplateOutputDTO) => {
              navigate(`/templates/generate/${item._id}`);
            },
          },
          {
            label: "Gerar Documento",
            icon: <IconRight />,
            onClick: (item: TemplateOutputDTO) => {
              setTemplate(item);
              setModal(true);
            },
          },
        ]}
      />
      {modal && template && (
        <ApplyTags
          template={template}
          isOpen={modal}
          onClose={() => setModal(false)}
        />
      )}

      <Dialog open={!!deleteId} onClose={cancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este template? Esta ação não pode ser
            desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TemplateList;
